import * as THREE from "../../../libs/three.js/build/three.module.js";
import {intersectGeometryWithPlane, boxIntersectsPlane} from "../SectionPlanes/SectionIntersection.js";
import {SUPPLY_COLORS} from "../LER/LER.js";

import {t} from "../../digitization/i18n/index.js";
// Ekstra linjer i 2D-profilvinduet (ProfileWindow): ud over punkterne i strimlen
// tegnes
//  - de tændte IFC/OBJ-modellers SNIT med profilens lodrette plan (skæringslinjer), og
//  - terrænlinjen fra 3D-baggrundskortet (BaseMap3D), hvis det er tændt.
// Alt er i profilens eget rum: x = afstand langs profilen ("mileage"), z = kote, y = 0
// (samme som punkterne i ProfileFakeOctree). Resultatet er flade Float32Array med
// linjesegmenter (6 tal pr. segment: x1,0,z1,x2,0,z2).

// Profilens segmenter, med samme geometri som ProfileRequest/ProfileData: lodret plan
// gennem hvert segment (kun xy tæller), mileage lagt sammen over segmenterne.
export function profileSegments(points){
	let segments = [];
	let mileage = 0;
	for(let i = 0; i < points.length - 1; i++){
		let start = points[i];
		let end = points[i + 1];
		let dx = end.x - start.x;
		let dy = end.y - start.y;
		let length = Math.sqrt(dx * dx + dy * dy);
		if(length < 1e-9){
			continue;
		}
		let dir = new THREE.Vector3(dx / length, dy / length, 0);
		let normal = new THREE.Vector3().crossVectors(dir, new THREE.Vector3(0, 0, 1)).normalize();
		segments.push({
			start: new THREE.Vector3(start.x, start.y, 0),
			dir,
			normal,
			length,
			mileage,
		});
		mileage += length;
	}
	return segments;
}

// Klipper et verdens-segment a-b (som ligger i segmentets lodrette plan) til segmentets
// udstrækning 0..length langs dir, og skriver det som profil-linje (mileage, 0, z).
// Returnerer true hvis noget blev skrevet.
export function pushClippedSegment(out, a, b, segment){
	let sx = segment.start.x;
	let sy = segment.start.y;
	let ta = (a.x - sx) * segment.dir.x + (a.y - sy) * segment.dir.y;
	let tb = (b.x - sx) * segment.dir.x + (b.y - sy) * segment.dir.y;

	let t0 = 0;
	let t1 = 1;
	let d = tb - ta;
	if(Math.abs(d) < 1e-9){
		// loddret linje i planet (fx en vaegkant): en lodret streg ved ét mileage
		if(ta < 0 || ta > segment.length){
			return false;
		}
		out.push(segment.mileage + ta, 0, a.z, segment.mileage + ta, 0, b.z);
		return true;
	}
	// parameter u (0..1 fra a til b) hvor t = 0 og t = length
	let uAtStart = (0 - ta) / d;
	let uAtEnd = (segment.length - ta) / d;
	t0 = Math.max(t0, Math.min(uAtStart, uAtEnd));
	t1 = Math.min(t1, Math.max(uAtStart, uAtEnd));
	if(t1 <= t0){
		return false;
	}

	let t0m = ta + d * t0;
	let t1m = ta + d * t1;
	out.push(
		segment.mileage + t0m, 0, a.z + (b.z - a.z) * t0,
		segment.mileage + t1m, 0, a.z + (b.z - a.z) * t1
	);
	return true;
}

let nextTick = () => new Promise(resolve => setTimeout(resolve, 0));

// Snit af en liste meshes med profilens planer. isCurrent() afbryder, hvis en nyere
// beregning er startet (der gives plads til skærmen hver ~8 ms).
export async function sectionMeshes(meshes, segments, isCurrent = () => true){
	let out = [];
	let box = new THREE.Box3();
	let checkpoint = performance.now();

	for(let mesh of meshes){
		let geometry = mesh.geometry;
		if(!geometry || !geometry.attributes || !geometry.attributes.position){
			continue;
		}
		if(!geometry.boundingBox){
			geometry.computeBoundingBox();
		}
		mesh.updateMatrixWorld(true);
		box.copy(geometry.boundingBox).applyMatrix4(mesh.matrixWorld);

		for(let segment of segments){
			if(!boxIntersectsPlane(box, segment.start, segment.normal)){
				continue;
			}
			// uden for segmentets udstrækning langs dir: spring over
			let c0 = (box.min.x - segment.start.x) * segment.dir.x + (box.min.y - segment.start.y) * segment.dir.y;
			let c1 = (box.max.x - segment.start.x) * segment.dir.x + (box.max.y - segment.start.y) * segment.dir.y;
			let c2 = (box.min.x - segment.start.x) * segment.dir.x + (box.max.y - segment.start.y) * segment.dir.y;
			let c3 = (box.max.x - segment.start.x) * segment.dir.x + (box.min.y - segment.start.y) * segment.dir.y;
			if(Math.max(c0, c1, c2, c3) < 0 || Math.min(c0, c1, c2, c3) > segment.length){
				continue;
			}

			for(let {a, b} of intersectGeometryWithPlane(geometry, mesh.matrixWorld, segment.start, segment.normal)){
				pushClippedSegment(out, a, b, segment);
			}
		}

		if(performance.now() - checkpoint > 8){
			await nextTick();
			if(!isCurrent()){
				return null;
			}
			checkpoint = performance.now();
		}
	}

	return new Float32Array(out);
}

// --- GIS (LER og andre lag) ----------------------------------------------------------------

const ELLIPSE_SEGMENTS = 24;
const MAX_ELLIPSE_STRETCH = 20;
const POINT_FEATURE_RADIUS = 0.125; // som COMPONENT_SPHERE_RADIUS i LER.js

function hexToRgb(hex){
	return [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];
}

// Snit af GIS-features (ledninger = cylindre om midterlinjen, komponenter = kugler) med profilens
// lodrette planer. features: LER-features ({geometry: "line"|"point", coords, diameter_m, supply,
// is_trace}), offset: lagets position (coords er relative til den). Et rør der skærer planet
// giver en ellipse (cirkel hvis vinkelret), et rør der ligger næsten i planet giver et bånd af to
// linjer, en komponent giver en cirkel. Returnerer {positions, colors} (linjesegmenter i
// profilrummet, farve pr. forsyningsart) eller null hvis afbrudt.
export async function sectionGisFeatures(features, offset, segments, isCurrent = () => true){
	let positions = [];
	let colors = [];
	let checkpoint = performance.now();

	let pushLine = (m1, z1, m2, z2, rgb) => {
		positions.push(m1, 0, z1, m2, 0, z2);
		colors.push(rgb[0], rgb[1], rgb[2], rgb[0], rgb[1], rgb[2]);
	};

	let pushEllipse = (cm, cz, major, minor, e1m, e1z, rgb) => {
		let e2m = -e1z;
		let e2z = e1m;
		let prevM = null;
		let prevZ = null;
		for(let k = 0; k <= ELLIPSE_SEGMENTS; k++){
			let theta = (k / ELLIPSE_SEGMENTS) * Math.PI * 2;
			let a = major * Math.cos(theta);
			let b = minor * Math.sin(theta);
			let m = cm + a * e1m + b * e2m;
			let z = cz + a * e1z + b * e2z;
			if(prevM !== null){
				pushLine(prevM, prevZ, m, z, rgb);
			}
			prevM = m;
			prevZ = z;
		}
	};

	for(let feature of features){
		if(feature.is_trace || !feature.coords || feature.coords.length === 0){
			continue;
		}

		let hex = SUPPLY_COLORS[feature.supply] !== undefined ? SUPPLY_COLORS[feature.supply] : SUPPLY_COLORS.andet;
		let rgb = hexToRgb(hex);
		let abs = feature.coords.map(c => [c[0] + offset[0], c[1] + offset[1], c[2] + offset[2]]);

		for(let segment of segments){
			let n = segment.normal;
			let dir = segment.dir;
			let sx = segment.start.x;
			let sy = segment.start.y;
			let distance = (p) => (p[0] - sx) * n.x + (p[1] - sy) * n.y;
			let along = (p) => (p[0] - sx) * dir.x + (p[1] - sy) * dir.y;

			if(feature.geometry !== "line"){
				let r = feature.diameter_m ? feature.diameter_m / 2 : POINT_FEATURE_RADIUS;
				let p = abs[0];
				let d = distance(p);
				let tm = along(p);
				if(Math.abs(d) < r && tm >= -r && tm <= segment.length + r){
					let rr = Math.sqrt(r * r - d * d);
					pushEllipse(segment.mileage + tm, p[2], rr, rr, 1, 0, rgb);
				}
				continue;
			}

			let r = Math.max(feature.diameter_m || 0.1, 0.02) / 2;
			for(let j = 0; j < abs.length - 1; j++){
				let p = abs[j];
				let q = abs[j + 1];
				let a = [q[0] - p[0], q[1] - p[1], q[2] - p[2]];
				let length = Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2]);
				if(length < 1e-9){
					continue;
				}
				let dp = distance(p);
				let dq = distance(q);
				let cosPhi = Math.abs(a[0] * n.x + a[1] * n.y) / length; // 1 = vinkelret paa planet, 0 = i planet

				if(cosPhi < 0.2){
					// (næsten) i planet: et bånd, hvis røret naar planet
					let d = (dp + dq) / 2;
					if(Math.abs(d) >= r){
						continue;
					}
					let rr = Math.sqrt(r * r - d * d);
					let tp = along(p);
					let tq = along(q);
					let axisM = tq - tp;
					let axisZ = q[2] - p[2];
					let axisLen = Math.sqrt(axisM * axisM + axisZ * axisZ);
					if(axisLen < 1e-9){
						continue;
					}
					let pm = -axisZ / axisLen;
					let pz = axisM / axisLen;
					for(let side of [-1, 1]){
						let m1 = tp + side * rr * pm;
						let z1 = p[2] + side * rr * pz;
						let m2 = tq + side * rr * pm;
						let z2 = q[2] + side * rr * pz;
						// klip til 0..length langs profilen
						let dm = m2 - m1;
						if(Math.abs(dm) < 1e-9){
							continue;
						}
						let u0 = Math.max(0, Math.min((0 - m1) / dm, (segment.length - m1) / dm));
						let u1 = Math.min(1, Math.max((0 - m1) / dm, (segment.length - m1) / dm));
						if(u1 <= u0){
							continue;
						}
						pushLine(segment.mileage + m1 + dm * u0, z1 + (z2 - z1) * u0, segment.mileage + m1 + dm * u1, z1 + (z2 - z1) * u1, rgb);
					}
					continue;
				}

				if(dp * dq > 0 || dp === dq){
					continue; // krydser ikke planet
				}
				let t = dp / (dp - dq);
				let x = [p[0] + a[0] * t, p[1] + a[1] * t, p[2] + a[2] * t];
				let tm = along(x);
				if(tm < -r || tm > segment.length + r){
					continue;
				}

				// ellipse: lille halvakse r, stor r / cos(vinkel) langs aksens projektion i planet
				let major = Math.min(r / cosPhi, r * MAX_ELLIPSE_STRETCH);
				let uDot = a[0] * n.x + a[1] * n.y;
				let um = (a[0] - uDot * n.x) * dir.x + (a[1] - uDot * n.y) * dir.y;
				let uz = a[2];
				let uLen = Math.sqrt(um * um + uz * uz);
				let e1m = uLen > 1e-9 ? um / uLen : 1;
				let e1z = uLen > 1e-9 ? uz / uLen : 0;
				pushEllipse(segment.mileage + tm, x[2], major, r, e1m, e1z, rgb);
			}
		}

		if(performance.now() - checkpoint > 8){
			await nextTick();
			if(!isCurrent()){
				return null;
			}
			checkpoint = performance.now();
		}
	}

	return {positions: new Float32Array(positions), colors: new Float32Array(colors)};
}

// --- kilder ------------------------------------------------------------------

// De tændte IFC/OBJ-elementer (host-API'et viewer.pvIfc.getMeshes() leverer kun synlige,
// ikke ghostede elementer, og intet hvis modellen er slukket).
export class IfcOverlay{
	constructor(viewer){
		this.viewer = viewer;
		this.id = "ifc";
		this.label = t("IFC-model");
		this.color = 0xff9a3c;
	}

	// findes der en IFC-model med synlige elementer?
	available(){
		let host = this.viewer.pvIfc;
		return !!(host && host.getMeshes && (host.getMeshes() || []).length > 0);
	}

	async compute(segments, isCurrent){
		let host = this.viewer.pvIfc;
		if(!host || !host.getMeshes){
			return null;
		}
		let meshes = host.getMeshes() || [];
		if(meshes.length === 0){
			return null;
		}
		return sectionMeshes(meshes, segments, isCurrent);
	}
}

// Terrænlinjen fra BaseMap3D (viewer.baseMap3D), hvis baggrundskortet er tændt.
export class BaseMapOverlay{
	constructor(viewer){
		this.viewer = viewer;
		this.id = "basemap";
		this.label = t("Baggrundskort (terræn)");
		this.color = 0x7bd45a;
	}

	// er baggrundskortet tændt?
	available(){
		return !!(this.viewer.baseMap3D && this.viewer.baseMap3D.sampleHeights);
	}

	async compute(segments, isCurrent){
		let baseMap = this.viewer.baseMap3D;
		if(!baseMap || !baseMap.sampleHeights){
			return null;
		}

		// prøvepunkter pr. segment: højst ca. hver meter, højst 1500 pr. segment
		let samples = [];
		for(let segment of segments){
			let n = Math.max(2, Math.min(1500, Math.ceil(segment.length / 1)) + 1);
			for(let i = 0; i < n; i++){
				let t = (i / (n - 1)) * segment.length;
				samples.push({
					x: segment.start.x + segment.dir.x * t,
					y: segment.start.y + segment.dir.y * t,
					mileage: segment.mileage + t,
					breakBefore: i === 0,
				});
			}
		}

		let heights = await baseMap.sampleHeights(samples);
		if(!heights || !isCurrent()){
			return null;
		}

		let out = [];
		for(let i = 1; i < samples.length; i++){
			if(samples[i].breakBefore){
				continue;
			}
			let z0 = heights[i - 1];
			let z1 = heights[i];
			if(!Number.isFinite(z0) || !Number.isFinite(z1)){
				continue; // hul (uden for zonen / ingen data): linjen brydes
			}
			out.push(samples[i - 1].mileage, 0, z0, samples[i].mileage, 0, z1);
		}
		return new Float32Array(out);
	}
}

// --- Tvaerprofiler (crossSectionTool) --------------------------------------------------

// 4 tal pr. segment (lateral1, z1, lateral2, z2) ud af profilrummets (x, 0, z)-par
function toLateralZ(lines){
	let out = new Float32Array((lines.length / 6) * 4);
	for(let i = 0, k = 0; i < lines.length; i += 6, k += 4){
		out[k] = lines[i];
		out[k + 1] = lines[i + 2];
		out[k + 2] = lines[i + 3];
		out[k + 3] = lines[i + 5];
	}
	return out;
}

// Tilfoejer station.overlays = [{id, label, color, lines}] til hvert tvaerprofil-station
// ({position: [x, y, z], tangent: [tx, ty]}): de tændte IFC-elementers snit med stationens
// lodrette plan (vinkelret paa forloebet) og terraenlinjen fra baggrundskortet, begge i
// stationens egne koordinater (lateral = tvaerafstand, z = kote) og klippet til
// +/- profileLength/2 - saa de kan tegnes direkte i profildiagrammet.
export async function addStationOverlays(viewer, stations, profileLength){
	let half = profileLength / 2;
	for(let station of stations){
		station.overlays = [];
	}

	let lateralOf = (station) => [-station.tangent[1], station.tangent[0]];

	// stationens lodrette plan (vinkelret paa forloebet) som profil-segment: mileage = lateral
	let stationSegment = (station) => {
		let lat = lateralOf(station);
		let [px, py] = station.position;
		let segments = profileSegments([
			{x: px - lat[0] * half, y: py - lat[1] * half},
			{x: px + lat[0] * half, y: py + lat[1] * half},
		]);
		if(segments.length > 0){
			segments[0].mileage = -half;
		}
		return segments;
	};

	// IFC
	let host = viewer && viewer.pvIfc;
	let meshes = (host && host.getMeshes && host.getMeshes()) || [];
	if(meshes.length > 0){
		for(let station of stations){
			let segments = stationSegment(station);
			if(segments.length === 0){
				continue;
			}
			let lines = await sectionMeshes(meshes, segments);
			if(lines && lines.length > 0){
				station.overlays.push({id: "ifc", label: t("IFC-model"), color: "#e07b00", lines: toLateralZ(lines)});
			}
		}
	}

	// GIS (LER m.fl.): snit af de taendte lag med stationens plane, farve pr. forsyningsart
	let gis = viewer && viewer.scene ? new GisOverlay(viewer) : null;
	let gisFeatures = gis ? gis.enabledFeatures() : [];
	if(gis && gisFeatures.length > 0){
		let offset = gis.layer().position.toArray();
		for(let station of stations){
			let segments = stationSegment(station);
			if(segments.length === 0){
				continue;
			}
			let result = await sectionGisFeatures(gisFeatures, offset, segments);
			if(result && result.positions.length > 0){
				let lines = toLateralZ(result.positions);
				let lineColors = new Float32Array((result.colors.length / 6) * 3);
				for(let i = 0, k = 0; i < result.colors.length; i += 6, k += 3){
					lineColors[k] = result.colors[i];
					lineColors[k + 1] = result.colors[i + 1];
					lineColors[k + 2] = result.colors[i + 2];
				}
				station.overlays.push({id: "gis", label: t("GIS"), color: "#555555", lines, lineColors});
			}
		}
	}

	// terraen
	let baseMap = viewer && viewer.baseMap3D;
	if(baseMap && baseMap.sampleHeights){
		let steps = Math.max(4, Math.min(200, Math.ceil(profileLength / 0.5)));
		let samples = [];
		for(let station of stations){
			let lat = lateralOf(station);
			let [px, py] = station.position;
			for(let i = 0; i <= steps; i++){
				let l = -half + (i / steps) * profileLength;
				samples.push({x: px + lat[0] * l, y: py + lat[1] * l});
			}
		}
		let heights = await baseMap.sampleHeights(samples);
		if(heights){
			for(let s = 0; s < stations.length; s++){
				let out = [];
				let base = s * (steps + 1);
				for(let i = 1; i <= steps; i++){
					let z0 = heights[base + i - 1];
					let z1 = heights[base + i];
					if(Number.isFinite(z0) && Number.isFinite(z1)){
						out.push(-half + ((i - 1) / steps) * profileLength, z0, -half + (i / steps) * profileLength, z1);
					}
				}
				if(out.length > 0){
					stations[s].overlays.push({id: "basemap", label: t("Terræn (baggrundskort)"), color: "#4c9a2a", lines: new Float32Array(out)});
				}
			}
		}
	}
}

// GIS-laget (LER og andre lag fra GIS-panelet): de tændte ledningsejeres/lags features som snit.
export class GisOverlay{
	constructor(viewer){
		this.viewer = viewer;
		this.id = "gis";
		this.label = t("GIS");
		this.color = 0xffffff; // farven kommer pr. forsyningsart (vertex-farver)
	}

	layer(){
		return this.viewer.scene.scene.children.find(o => o.name === "ler_layer") || null;
	}

	enabledFeatures(){
		let layer = this.layer();
		if(!layer){
			return [];
		}
		let features = [];
		for(let group of layer.owners.values()){
			if(group.enabled){
				features.push(...group.features);
			}
		}
		return features;
	}

	available(){
		return this.enabledFeatures().length > 0;
	}

	async compute(segments, isCurrent){
		let layer = this.layer();
		let features = this.enabledFeatures();
		if(!layer || features.length === 0){
			return null;
		}
		return sectionGisFeatures(features, layer.position.toArray(), segments, isCurrent);
	}
}
